import {
  Injectable,
  makeStateKey,
  TransferState,
  inject,
  signal,
  computed,
  Injector,
  REQUEST_CONTEXT,
  PLATFORM_ID,
} from '@angular/core';

import {
  DEFAULT_LANGUAGE,
  DEFAULT_LOCALE_ID,
  getCurrencyLocaleDataByCountryCode,
  LocaleID,
  SubsidiarySelectionSource,
  SUPPORTED_LANGUAGES,
} from '@pedix-workspace/utils';
import {
  ApiV1_Catalog,
  ApiV1_Product,
  fetchCatalog,
  getCatalogCategories,
  getCatalogProducts,
} from '@pedix-workspace/api-v1';
import { environment } from '../environments/environment';
import { AppCartItemsService } from './app-cart-items.service';
import { validatePricingChanges } from '@pedix-workspace/orders';
import { SessionAppDataService } from '@pedix-workspace/pedixapp-core-services';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { cloneDeep } from 'lodash-es';
import { convertEstablishmentData } from '@pedix-workspace/shared-models';

const STATE_KEY_APP_REQUEST_CONTEXT = makeStateKey<AppRequestContext>('app-request-context-state');

export type SwitchSubsidiaryCatalogParams = {
  establishmentId: string;
  isUserSelected: boolean;
};

export type AppRequestContext = {
  baseHref: string;
  hostname: string;
  pathname: string;
  source: 'path' | 'subdomain' | 'domain';
  slug: string;
  catalog: ApiV1_Catalog | null;
  subsidiaryParentCatalog?: ApiV1_Catalog;
  subsidiarySelectionSource?: SubsidiarySelectionSource;
  translations: Record<string, string | Record<string, string>>;
  localeId: LocaleID;
  selectedLanguage: SUPPORTED_LANGUAGES;
  localeData: any;
};

const DEFAULT_CONTEXT: AppRequestContext = {
  baseHref: '',
  hostname: '',
  pathname: '',
  source: 'path',
  slug: '',
  catalog: null,
  translations: {},
  localeId: DEFAULT_LOCALE_ID,
  selectedLanguage: DEFAULT_LANGUAGE,
  localeData: {},
};

@Injectable({ providedIn: 'root' })
export class AppRequestContextService {
  private readonly state = inject(TransferState);
  private readonly injector = inject(Injector);
  private readonly sessionAppDataService = inject(SessionAppDataService);
  private readonly platformId = inject(PLATFORM_ID);

  // NOTE: these getters prevents a circular dependency caused by NgxLogger DI configuration in app.config.ts
  get cartItemsService() {
    return this.injector.get(AppCartItemsService);
  }
  // ------------------------------------------------------------

  readonly #appRequestContext = signal<AppRequestContext>(DEFAULT_CONTEXT);

  // NOTE: this was moved from EstablishmentSharedService when removed this dependency from pedixapp app
  // This probably shouldn't be here, but for the moment it's OK
  // This is a flag only used when previewing the "client app" from the "admin app" to tweak some UI elements/stylings
  readonly previewMode = signal<boolean>(false);

  readonly source = computed(() => this.#appRequestContext().source);
  readonly slug = computed(() => this.#appRequestContext().slug);
  readonly hostname = computed(() => this.#appRequestContext().hostname);
  readonly pathname = computed(() => this.#appRequestContext().pathname);
  readonly localeId = computed(() => this.#appRequestContext().localeId);
  readonly localeData = computed(() => this.#appRequestContext().localeData);
  readonly selectedLanguage = computed(() => this.#appRequestContext().selectedLanguage);
  readonly translations = computed(() => this.#appRequestContext().translations);
  readonly baseHref = computed(() => this.#appRequestContext().baseHref);

  readonly catalog = computed(() => this.#appRequestContext().catalog);
  readonly subsidiaryParentCatalog = computed(
    () => this.#appRequestContext().subsidiaryParentCatalog,
  );
  readonly subsidiarySelectionSource = computed(
    () => this.#appRequestContext().subsidiarySelectionSource,
  );
  readonly isSubsidiaryIdFromTrustedSource = computed(() => {
    const subsidiarySelectionSource = this.subsidiarySelectionSource();

    return (
      subsidiarySelectionSource === 'query' ||
      subsidiarySelectionSource === 'cookie' ||
      subsidiarySelectionSource === 'last-order'
    );
  });
  readonly subsidiarySelector = computed(
    () => this.subsidiaryParentCatalog()?.establishment?.subsidiarySelector || false,
  );
  readonly stockCheckDisabled = computed(() => {
    if (this.subsidiarySelector()) {
      return !this.isSubsidiaryIdFromTrustedSource();
    }
    return false;
  });

  readonly establishment = computed(() => this.catalog()?.establishment || null);
  readonly subsidiaries = computed(
    () => this.subsidiaryParentCatalog()?.subsidiaries || this.catalog()?.subsidiaries || null,
  );
  readonly selectedSubsidiary = computed(
    () =>
      this.subsidiaries()?.find(subsidiary => subsidiary.id === this.establishment()?.id) || null,
  );
  readonly allCategories = computed(() =>
    getCatalogCategories(this.catalog(), {
      dynamicCategories: 'included',
      categories: 'all',
      products: 'all',
      emptyCategories: 'kept',
    }),
  );
  readonly activeCategories = computed(() =>
    getCatalogCategories(this.catalog(), {
      dynamicCategories: 'included',
      categories: 'active',
      products: 'all',
      emptyCategories: 'dropped',
    }),
  );
  readonly allCategoriesWithActiveProducts = computed(() =>
    getCatalogCategories(this.catalog(), {
      dynamicCategories: 'included',
      categories: 'all',
      products: 'active',
      emptyCategories: 'kept',
    }),
  );
  readonly allProducts = computed(() =>
    getCatalogProducts(this.catalog(), {
      dynamicCategories: 'excluded',
      categories: 'all',
      products: 'all',
    }),
  );
  readonly activeProducts = computed(() =>
    getCatalogProducts(this.catalog(), {
      dynamicCategories: 'excluded',
      categories: 'active',
      products: 'active',
    }),
  );
  readonly productTags = computed(() => this.catalog()?.productTags);
  readonly currencyLocaleData = computed(() => {
    const establishment = this.establishment();
    return getCurrencyLocaleDataByCountryCode(establishment?.currencyCode);
  });
  readonly shouldDisplaySku = computed(() => {
    const establishment = this.establishment();
    return (
      establishment?.catalogConfiguration.displaySku &&
      establishment?.catalogConfiguration.skuInOrderMessage
    );
  });
  readonly availableIntegrations = computed(() => this.catalog()?.availableIntegrations || null);
  readonly promotions = computed(() => this.catalog()?.promotions || []);
  readonly hasCoupons = computed(() => this.catalog()?.hasCoupons || false);

  constructor() {
    if (isPlatformServer(this.platformId)) {
      const appRequestContextData =
        (inject(REQUEST_CONTEXT, { optional: true }) as AppRequestContext | null) ||
        DEFAULT_CONTEXT;

      this.updateContext(appRequestContextData);

      this.state.set(STATE_KEY_APP_REQUEST_CONTEXT, appRequestContextData);
    } else if (isPlatformBrowser(this.platformId)) {
      const contextFromState = this.state.get(STATE_KEY_APP_REQUEST_CONTEXT, DEFAULT_CONTEXT);

      // NOTE: we need to convert data after SSR to de-serialize values, like dates
      const establishment = contextFromState.catalog?.establishment
        ? convertEstablishmentData(
            contextFromState.catalog?.establishment?.id,
            contextFromState.catalog?.establishment,
          )
        : null;

      this.updateContext({
        ...contextFromState,
        catalog: {
          ...contextFromState.catalog,
          establishment,
        },
      });
    }
  }

  async switchSubsidiaryCatalog({
    establishmentId,
    isUserSelected,
  }: SwitchSubsidiaryCatalogParams) {
    // NOTE: we might want to call this method with same establishmentId to force setting isUserSelected
    // rest of the logic would be redundant, but not a big deal
    const isSameEstablishment = establishmentId === this.establishment().id;
    // 1- Fetch the new catalog (or use current one if establishment hasn't changed)
    const selectedSubsidiaryCatalog = isSameEstablishment
      ? this.catalog()
      : await fetchCatalog({
          apiUrl: environment.apiV1Url,
          establishmentId,
          selectedLanguage: this.selectedLanguage(),
        });

    if (!selectedSubsidiaryCatalog) {
      alert('No se pudo obtener el catálogo de la sucursal');

      return null;
    }

    // 2- Updates catalog state
    this.updateContext({ catalog: selectedSubsidiaryCatalog });

    // 3- Stores the selected subsidiary id in a cookie
    await this.sessionAppDataService.setSelectedSubsidiaryId(
      this.subsidiaryParentCatalog().establishment.slug,
      establishmentId,
      isUserSelected,
    );
    this.updateContext({ subsidiarySelectionSource: isUserSelected ? 'cookie' : 'cookie-auto' });
    // ----

    const selectedSubsidiaryProducts = this.activeProducts();
    const oldCartItems = this.cartItemsService.cartItems().slice(0);

    // 4- Updates cartItems with the selected subsidiary products (maps categories, products, presentations, options and tags using clonedFrom field)
    const { updatedCartItems, removedCartItems } =
      this.cartItemsService.getUpdatedCartItemsForSubsidiary(selectedSubsidiaryCatalog);

    this.cartItemsService.setCartItems(updatedCartItems);

    // 6- Validates pricing changes comparing cartItems with the new subsidiary products
    const pricingErrors = validatePricingChanges(selectedSubsidiaryProducts, oldCartItems);

    return {
      pricingErrors,
      removedCartItems,
      updatedCartItems,
    };
  }

  /**
   * Actualiza el estado del catálogo mediante una petición HTTP.
   * Mantiene el resto del contexto y solo actualiza el catálogo.
   */
  async refreshCatalogState(): Promise<void> {
    const slug = this.establishment().slug;
    const selectedLanguage = this.selectedLanguage();

    if (!slug) {
      throw new Error('Cannot refresh catalog state: slug is not available');
    }

    const catalog = await fetchCatalog({
      apiUrl: environment.apiV1Url,
      slug,
      selectedLanguage,
    });

    if (catalog) {
      this.updateContext({ catalog });
    }
  }

  private updateContext(updatedContext: Partial<AppRequestContext>) {
    this.#appRequestContext.update(currentContext =>
      cloneDeep({
        ...currentContext,
        ...updatedContext,
      }),
    );
  }
}
